/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useCallback } from 'react';
import useSWR, { SWRConfiguration } from 'swr';
import { useApolloClient, useQuery, ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { isServer } from '@marriott/mi-apollo-client-utils';
import { fetchHQVPhotogallery, HQVFetcherContext } from './hqvFetchers';
import { useConditionalPageContextSERP } from './useConditionalPageContext';
import { usePersistentGlobalStore } from '@marriott/mi-store-utils';
import { useHQVStore } from '../../modules/store/hqvZustandStore';
import { phoenixShopHQVPhotogalleryCall } from '../../organisms/HotelGalleryTitle/HQVQueries';
import {
  SWR_ERROR_RETRY_COUNT,
  SWR_ERROR_RETRY_INTERVAL,
  hqvLog,
  isHqvDebugEnabled,
} from '../../constants/lib/constants';

export interface UseHQVPhotogalleryOptions {
  enabled?: boolean;
  cachedPropertyMedia?: any;
  swrConfig?: SWRConfiguration;
}

export interface UseHQVPhotogalleryResult {
  photogallery: any;
  isLoading: boolean;
  isValidating: boolean;
  error: Error | undefined;
  refetch: () => Promise<any>;
}

/**
 * Custom hook for fetching HQV Photogallery data
 * Supports both SWR mode (new) and Apollo useQuery mode (legacy)
 *
 * @param propertyId - The property ID to fetch photos for (null to skip fetching)
 * @param options - Configuration options including cached data
 * @returns Photogallery data, loading state, error, and refetch function
 *
 * @example
 * ```tsx
 * const { photogallery, isLoading, error } = useHQVPhotogallery(propertyId);
 *
 * if (isLoading) return <Spinner />;
 * if (error) return <Error />;
 * return <PhotoGallery data={photogallery} />;
 * ```
 */
export function useHQVPhotogallery(
  propertyId: string | null,
  options: UseHQVPhotogalleryOptions = {}
): UseHQVPhotogalleryResult {
  const { enabled = true, cachedPropertyMedia, swrConfig = {} } = options;

  // Check if SWR mode is enabled from the store
  const enableUseSWR = useHQVStore(state => state.hqvState.enableUseSWR);

  const client = useApolloClient();
  const { requestId, currentLocale, sessionData } = useConditionalPageContextSERP();
  const globalData = usePersistentGlobalStore((state: any) => state.globalData);

  const sessionToken =
    sessionData?.sessionToken || sessionData?.cacheData?.key || globalData?.sessionData?.sessionToken || '';

  // Use fallback locale for consistency
  const localeForContext = currentLocale || 'en-US';

  const context: HQVFetcherContext = {
    requestId,
    locale: localeForContext,
    sessionToken,
  };

  // ============================================================================
  // SWR MODE
  // ============================================================================
  const shouldFetchSWR = enableUseSWR && enabled && !!propertyId && !cachedPropertyMedia;
  const swrKey = shouldFetchSWR ? ['hqv-photogallery', propertyId] : null;

  const {
    data: swrData,
    error: swrError,
    isLoading: swrIsLoading,
    isValidating: swrIsValidating,
    mutate: swrMutate,
  } = useSWR(
    swrKey,
    async ([, id]: [string, string]) => {
      if (isHqvDebugEnabled()) {
        hqvLog.log('useHQVPhotogallery - [SWR Mode] Fetcher executing for:', id);
      }
      const result = await fetchHQVPhotogallery(client as ApolloClient<NormalizedCacheObject>, id, context);
      if (isHqvDebugEnabled()) {
        hqvLog.log('useHQVPhotogallery - [SWR Mode] Fetcher completed for:', id, { hasData: !!result });
      }
      return result;
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 5000,
      errorRetryCount: SWR_ERROR_RETRY_COUNT,
      errorRetryInterval: SWR_ERROR_RETRY_INTERVAL,
      ...swrConfig,
    }
  );

  // ============================================================================
  // LEGACY MODE (Apollo useQuery)
  // ============================================================================
  const shouldFetchLegacy = !enableUseSWR && enabled && !!propertyId && !cachedPropertyMedia;

  const {
    data: legacyData,
    error: legacyError,
    loading: legacyLoading,
    refetch: legacyRefetch,
  } = useQuery(phoenixShopHQVPhotogalleryCall, {
    skip: !shouldFetchLegacy,
    fetchPolicy: isServer ? 'network-only' : 'cache-first',
    variables: {
      propertyId: propertyId,
    },
    context: {
      headers: {
        'x-request-id': requestId || '',
        'x-session-id': sessionToken || '',
      },
    },
  });

  // Log legacy mode state
  useEffect(() => {
    if (isHqvDebugEnabled() && shouldFetchLegacy) {
      hqvLog.log('useHQVPhotogallery - [Legacy Mode] Apollo state:', {
        propertyId,
        isLoading: legacyLoading,
        hasData: !!legacyData,
        hasError: !!legacyError,
      });
    }
  }, [shouldFetchLegacy, propertyId, legacyLoading, legacyData, legacyError]);

  // ============================================================================
  // UNIFIED RETURN
  // ============================================================================
  const refetch = useCallback(async () => {
    if (enableUseSWR) {
      hqvLog.log('useHQVPhotogallery - [SWR Mode] Refetching for:', propertyId);
      return swrMutate();
    } else {
      hqvLog.log('useHQVPhotogallery - [Legacy Mode] Refetching for:', propertyId);
      return legacyRefetch();
    }
  }, [enableUseSWR, propertyId, swrMutate, legacyRefetch]);

  // Handle cached property media (same for both modes)
  if (cachedPropertyMedia) {
    return {
      photogallery: { property: cachedPropertyMedia },
      isLoading: false,
      isValidating: false,
      error: undefined,
      refetch,
    };
  }

  // Return based on mode
  if (enableUseSWR) {
    return {
      photogallery: swrData,
      isLoading: swrIsLoading,
      isValidating: swrIsValidating,
      error: swrError,
      refetch,
    };
  } else {
    return {
      photogallery: legacyData,
      isLoading: legacyLoading,
      isValidating: false,
      error: legacyError as Error | undefined,
      refetch,
    };
  }
}
